/* Shared bits for Management Accounts editor — colours, formatting helpers,
   and tiny presentational components used by every step. */
import React, { useState, useRef } from "react";
import { CheckCircle } from "@phosphor-icons/react";

export const C = {
  bgDark: "#000",
  panel: "#0A2826",
  panelHover: "#0D3330",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

export const kes = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const sign = v < 0 ? "(" : "";
  const close = v < 0 ? ")" : "";
  return `${sign}KES ${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${close}`;
};

export function Header({ step, title, desc }) {
  return (
    <div>
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: C.orange }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span> Step {step}
      </div>
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ letterSpacing: "-0.02em" }}>{title}</h2>
      <p className="text-white/55 mt-2 max-w-3xl">{desc}</p>
    </div>
  );
}

export function Panel({ title, children }) {
  return (
    <section className="rounded-2xl border p-6" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
      {title && <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-4" style={{ color: C.tealSoft }}>{title}</div>}
      {children}
    </section>
  );
}

export function GateBar({ gateId, done, onConfirm, requirements }) {
  return (
    <div className="rounded-xl border p-5 flex items-center justify-between" data-testid={`gate-${gateId}`}
      style={{ borderColor: done ? "rgba(10,139,133,0.5)" : "rgba(255,255,255,0.1)", backgroundColor: done ? "rgba(10,139,133,0.08)" : "rgba(0,0,0,0.25)" }}>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: done ? C.teal : C.orange }}>
          {done ? "Gate check passed" : "Gate check pending"}
        </div>
        <ul className="text-white/55 text-xs mt-2 space-y-0.5">
          {requirements.map((r, i) => <li key={i}>· {r}</li>)}
        </ul>
      </div>
      {!done && (
        <button onClick={onConfirm} data-testid={`confirm-gate-${gateId}`}
          className="px-4 py-2 rounded-md font-semibold text-xs"
          style={{ backgroundColor: C.teal, color: "#fff" }}>Confirm step</button>
      )}
      {done && <CheckCircle size={28} weight="fill" style={{ color: C.teal }} />}
    </div>
  );
}

export function UploadCard({ label, source, count, onFile, testid, busy }) {
  const ref = useRef(null);
  const [replace, setReplace] = useState(false);
  return (
    <div className="rounded-xl p-5 border-2 border-dashed text-left transition-colors"
      style={{ borderColor: count > 0 ? C.teal : "rgba(255,255,255,0.18)", backgroundColor: count > 0 ? "rgba(10,139,133,0.08)" : "transparent" }}
      data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: count > 0 ? C.teal : C.tealSoft }}>
        {label} · <span className="font-mono-num">{source}</span>
      </div>
      <button onClick={() => ref.current?.click()} disabled={busy}
        className="block text-left w-full hover:opacity-80 disabled:opacity-50">
        <div className="text-2xl font-extrabold" style={{ color: count > 0 ? C.teal : "rgba(255,255,255,0.4)" }}>
          {count} rows
        </div>
        <div className="text-xs text-white/45 mt-1">CSV or XLSX · click to upload</div>
      </button>
      {count > 0 && (
        <label className="text-[10px] text-white/55 flex items-center gap-1.5 mt-2 cursor-pointer">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)}
            data-testid={`${testid}-replace`} className="accent-[#0A8B85]" />
          Replace existing {source} rows on next upload
        </label>
      )}
      <input ref={ref} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }}
        onChange={(e) => onFile(e.target.files?.[0], replace)} />
    </div>
  );
}

export function NumField({ label, v, on, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45 mb-1.5">{label}</div>
      <input type="number" step="0.01" value={v ?? 0} onChange={(e) => on(Number(e.target.value))}
        data-testid={testid}
        className="w-full px-3 py-2 rounded-md text-sm font-mono-num focus:outline-none focus:border-[#0A8B85]"
        style={{ backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff" }} />
    </div>
  );
}

export function Row({ label, v, bold, warn }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
      <span className="text-white/75">{label}</span>
      <span className={`font-mono-num ${bold ? "font-bold" : ""}`} style={{ color: warn ? "#F87171" : (bold ? C.teal : "#fff") }}>{v}</span>
    </div>
  );
}
